/**
 * useSoundEffects — maps sound types to SoundEngine methods
 * Reads settings from useSoundSettings to respect mute/volume/quiet hours.
 */

import { useCallback } from 'react';
import { SoundEngine } from '../audio';
import { useSoundSettings } from './useSoundSettings';

export type SoundType =
  // Backward-compatible
  | 'tap' | 'send' | 'receive' | 'celebrate' | 'achievement' | 'error'
  // New interaction
  | 'toggle' | 'navigate' | 'success'
  // Celebration
  | 'taskChime' | 'routineJingle' | 'streakFanfare' | 'rewardSparkle'
  // Companion
  | 'happyHum' | 'encouragingMmHmm' | 'celebratoryWoohoo' | 'gentleOh';

const companionSounds = new Set<SoundType>([
  'happyHum', 'encouragingMmHmm', 'celebratoryWoohoo', 'gentleOh',
]);

export function useSoundEffects() {
  const { sfxEnabled, companionEnabled, effectivelyMuted } = useSoundSettings();

  const play = useCallback(
    (sound: SoundType) => {
      if (effectivelyMuted()) return;

      const isCompanion = companionSounds.has(sound);
      if (isCompanion && !companionEnabled) return;
      if (!isCompanion && !sfxEnabled) return;

      const engine = SoundEngine.getInstance();

      switch (sound) {
        // Interaction
        case 'tap':
        case 'send':
          engine.buttonPop();
          break;
        case 'receive':
          engine.successDing();
          break;
        case 'toggle':
          engine.toggleClick();
          break;
        case 'navigate':
          engine.navigationWhoosh();
          break;
        case 'success':
          engine.successDing();
          break;
        case 'error':
          engine.errorBonk();
          break;
        // Celebration
        case 'celebrate':
          engine.routineJingle();
          break;
        case 'achievement':
          engine.rewardSparkle();
          break;
        case 'taskChime':
          engine.taskChime();
          break;
        case 'routineJingle':
          engine.routineJingle();
          break;
        case 'streakFanfare':
          engine.streakFanfare();
          break;
        case 'rewardSparkle':
          engine.rewardSparkle();
          break;
        // Companion
        case 'happyHum':
          engine.happyHum();
          break;
        case 'encouragingMmHmm':
          engine.encouragingMmHmm();
          break;
        case 'celebratoryWoohoo':
          engine.celebratoryWoohoo();
          break;
        case 'gentleOh':
          engine.gentleOh();
          break;
      }
    },
    [sfxEnabled, companionEnabled, effectivelyMuted],
  );

  return { play };
}
